/**
 * Shared utilities for common operations across the application
 * Eliminates code duplication and provides consistent behavior
 */

import { logError, logWarning } from './error-handler.js';

/**
 * Object utilities for common object operations
 */
export class ObjectUtils {
    /**
     * Check if an object is empty (has no enumerable properties)
     * @param {Object} obj - The object to check
     * @returns {boolean} True if object is empty
     */
    static isEmpty(obj) {
        if (!obj || typeof obj !== 'object') {
            return true;
        }
        return Object.keys(obj).length === 0;
    }

    /**
     * Check if an object has any enumerable properties
     * @param {Object} obj - The object to check
     * @returns {boolean} True if object has properties
     */
    static hasProperties(obj) {
        return !this.isEmpty(obj);
    }

    /**
     * Get the number of enumerable properties in an object
     * @param {Object} obj - The object to count
     * @returns {number} Number of properties
     */
    static getPropertyCount(obj) {
        if (!obj || typeof obj !== 'object') {
            return 0;
        }
        return Object.keys(obj).length;
    }
}

/**
 * Kendo widget utilities for common Kendo UI operations
 */
export class KendoUtils {
    /**
     * List of supported Kendo widget types
     */
    static get SUPPORTED_WIDGETS() {
        return [
            'kendoDropDownList',
            'kendoMultiSelect',
            'kendoComboBox',
            'kendoSwitch',
            'kendoDatePicker',
            'kendoDateRangePicker'
        ];
    }

    /**
     * Apply a value to a Kendo widget
     * @param {jQuery} $element - jQuery element containing the widget
     * @param {any} value - Value to apply
     * @returns {boolean} True if value was applied successfully
     */
    static applyValueToWidget($element, value) {
        try {
            for (const widgetType of this.SUPPORTED_WIDGETS) {
                const widget = $element.data(widgetType);
                if (widget && typeof widget.value === 'function') {
                    widget.value(value);
                    return true;
                }
            }
            return false;
        } catch (error) {
            logWarning('Failed to apply value to widget', { error: error.message });
            return false;
        }
    }

    /**
     * Check if an element has any Kendo widget
     * @param {jQuery} $element - jQuery element to check
     * @returns {boolean} True if element has a Kendo widget
     */
    static hasKendoWidget($element) {
        try {
            return this.SUPPORTED_WIDGETS.some(widgetType => {
                return $element.data(widgetType) !== undefined;
            });
        } catch (error) {
            logWarning('Failed to check for Kendo widget', { error: error.message });
            return false;
        }
    }

    /**
     * Get the first available Kendo widget from an element
     * @param {jQuery} $element - jQuery element to check
     * @returns {Object|null} The first found widget or null
     */
    static getFirstKendoWidget($element) {
        try {
            for (const widgetType of this.SUPPORTED_WIDGETS) {
                const widget = $element.data(widgetType);
                if (widget) {
                    return widget;
                }
            }
            return null;
        } catch (error) {
            logWarning('Failed to get Kendo widget', { error: error.message });
            return null;
        }
    }

    /**
     * Get the widget type of the first available Kendo widget
     * @param {jQuery} $element - jQuery element to check
     * @returns {string|null} The widget type or null
     */
    static getKendoWidgetType($element) {
        try {
            for (const widgetType of this.SUPPORTED_WIDGETS) {
                const widget = $element.data(widgetType);
                if (widget) {
                    return widgetType;
                }
            }
            return null;
        } catch (error) {
            logWarning('Failed to get Kendo widget type', { error: error.message });
            return null;
        }
    }
}

/**
 * Form utilities for common form operations
 */
export class FormUtils {
    /**
     * Apply a value to a form element (HTML input, select, or Kendo widget)
     * @param {HTMLElement} element - The form element
     * @param {any} value - Value to apply
     * @returns {boolean} True if value was applied successfully
     */
    static applyValueToElement(element, value) {
        try {
            if (!element) {
                return false;
            }

            // Check for Kendo widgets FIRST (especially important for SELECT elements like MultiSelect)
            const $element = $(element);
            if (KendoUtils.hasKendoWidget($element)) {
                return KendoUtils.applyValueToWidget($element, value);
            }

            // Handle HTML form elements (only if no Kendo widget found)
            if (element.tagName === 'INPUT') {
                if (
                    element.type === 'text' ||
                    element.type === 'email' ||
                    element.type === 'search' ||
                    element.type === 'number'
                ) {
                    element.value = value || '';
                    return true;
                } else if (element.type === 'checkbox') {
                    element.checked = Boolean(value);
                    return true;
                }
            } else if (element.tagName === 'SELECT') {
                // For plain SELECT without Kendo widget
                element.value = value || '';
                return true;
            }

            return false;
        } catch (error) {
            logWarning('Failed to apply value to element', { error: error.message });
            return false;
        }
    }

    /**
     * Apply multiple values to form elements
     * @param {Object} values - Object with element IDs as keys and values as values
     * @returns {number} Number of values successfully applied
     */
    static applyValuesToForm(values) {
        let appliedCount = 0;

        try {
            Object.keys(values).forEach(id => {
                const element = document.getElementById(id);
                if (element && this.applyValueToElement(element, values[id])) {
                    appliedCount++;
                }
            });
        } catch (error) {
            logError(error);
        }

        return appliedCount;
    }
}

/**
 * Session storage utilities for common session storage operations
 */
export class SessionStorageUtils {
    /**
     * Check if session storage is available
     * @returns {boolean} True if session storage is available
     */
    static isAvailable() {
        try {
            return typeof Storage !== 'undefined' && window.sessionStorage !== null;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get fallback values from session storage if primary values are empty
     * @param {Object} primaryValues - Primary values to check
     * @param {Function} loadFunction - Function to load from session storage
     * @returns {Object} Primary values or fallback values from session storage
     */
    static getFallbackValues(primaryValues, loadFunction) {
        try {
            // If primary values are not empty, return them
            if (ObjectUtils.hasProperties(primaryValues)) {
                return primaryValues;
            }

            // Try to get fallback values from session storage
            if (this.isAvailable() && typeof loadFunction === 'function') {
                const savedValues = loadFunction();
                if (ObjectUtils.hasProperties(savedValues)) {
                    return savedValues;
                }
            }

            return primaryValues;
        } catch (error) {
            logWarning('Failed to get fallback values', { error: error.message });
            return primaryValues;
        }
    }
}

/**
 * Error handling utilities for consistent error handling patterns
 */
export class ErrorUtils {
    /**
     * Execute a function with error handling and logging
     * @param {Function} fn - Function to execute
     * @param {string} context - Context for error logging
     * @param {any} fallbackValue - Value to return on error
     * @returns {any} Function result or fallback value
     */
    static safeExecute(fn, context, fallbackValue = null) {
        try {
            return fn();
        } catch (error) {
            logError(error, { operation: context });
            return fallbackValue;
        }
    }

    /**
     * Execute an async function with error handling and logging
     * @param {Function} fn - Async function to execute
     * @param {string} context - Context for error logging
     * @param {any} fallbackValue - Value to return on error
     * @returns {Promise<any>} Function result or fallback value
     */
    static async safeExecuteAsync(fn, context, fallbackValue = null) {
        try {
            return await fn();
        } catch (error) {
            logError(error, { operation: context });
            return fallbackValue;
        }
    }
}

/**
 * DOM utilities for common DOM operations
 */
export class DOMUtils {
    /**
     * Find all elements matching a selector
     * @param {string} selector - CSS selector
     * @returns {jQuery} jQuery object with matching elements
     */
    static findElements(selector) {
        try {
            return $(selector);
        } catch (error) {
            logWarning(`Failed to find elements with selector "${selector}"`, {
                selector,
                error: error.message
            });
            return $();
        }
    }

    /**
     * Get element by ID
     * @param {string} id - Element ID
     * @returns {HTMLElement|null} Element or null
     */
    static getElementById(id) {
        try {
            return document.getElementById(id);
        } catch (error) {
            logWarning(`Failed to get element by ID "${id}"`, { id, error: error.message });
            return null;
        }
    }

    /**
     * Check if an element exists in the DOM
     * @param {string} selector - CSS selector
     * @returns {boolean} True if element exists
     */
    static elementExists(selector) {
        try {
            return $(selector).length > 0;
        } catch (error) {
            logWarning(`Failed to check element existence "${selector}"`, {
                selector,
                error: error.message
            });
            return false;
        }
    }
}

// Export all utilities as a single object for convenience
export default {
    ObjectUtils,
    KendoUtils,
    FormUtils,
    SessionStorageUtils,
    ErrorUtils,
    DOMUtils
};
